import { useCallback, useEffect, useRef, useState } from 'react';
import useAxios from '@hooks/useFetch';
import { downloadFileFromBlobResponse } from '@core/utils/file';
import { useDebouncedCallback } from '@hooks/useDebouncedCallback';
import { components } from "@api/api";
import { Sort } from "@models";
export type MenuItemExample = components["schemas"]["Application.Byo.Models.MenuExamplesOutputModel.MenuItemExample"]

export const TAKE = 100;
const INITIAL_TOTAL_COUNT_EXAMPLES = 30;
const DEFAULT_SORT_PARAMS = {
    direction: Sort.Asc as const,
    column: 0,
};

type SortParams = {
    direction: Sort.Asc | Sort.Desc;
    column: number;
};

type FetchOptions = {
    sortColumn: number;
    search: string;
    take: number;
    skip: number;
    sortDir: string;
};

type FetchResponse = {
    data: MenuItemExample[];
    totalRecords: number;
};

type AbortablePromise<T = unknown> = Promise<T> & {
    abort?: () => void;
};

export interface UseMenuExamplesPopupDataParams {
    fetch: (options: FetchOptions) => AbortablePromise<FetchResponse>;
    addNotification: (notification: unknown) => void;
    isShown: boolean;
    fetchExport?: (options: FetchOptions) => Promise<string | void>;
    downloadUrl?: string;
}

export const useMenuExamplesPopupData = ({
    fetch,
    addNotification: addNotificationProp,
    isShown,
    fetchExport,
    downloadUrl,
}: UseMenuExamplesPopupDataParams) => {
    const [isLoading, setIsLoading] = useState(true);
    const [tableData, setTableData] = useState<MenuItemExample[]>([]);
    const [totalCountExamples, setTotalCountExamples] = useState(INITIAL_TOTAL_COUNT_EXAMPLES);
    const [sort, setSort] = useState<SortParams>(DEFAULT_SORT_PARAMS);
    const [searchQuery, setSearchQuery] = useState('');

    const requestExamplesRef = useRef<AbortablePromise<FetchResponse> | null>(null);

    // Use refs for functions that come from props to avoid dependency changes triggering effects
    const fetchRef = useRef(fetch);
    fetchRef.current = fetch;
    const fetchExportRef = useRef(fetchExport);
    fetchExportRef.current = fetchExport;

    // useAxios for blob download - abort handled automatically by the hook
    const { doFetch: doDownload, isLoading: isDownloading } = useAxios<Blob>({
        url: downloadUrl ?? '',
        method: 'get',
        lazy: true,
        initialConfig: {
            responseType: 'blob',
        },
    });

    const notify = useCallback(
        (notification: unknown) => addNotificationProp(notification),
        [addNotificationProp]
    );

    // Use ref to track current skip value to avoid circular dependency
    const skipRef = useRef(0);

    const getFetchOptions = useCallback(
        (skip: number = skipRef.current): FetchOptions => ({
            sortColumn: sort.column,
            search: searchQuery,
            take: TAKE,
            skip,
            sortDir: sort.direction,
        }),
        [sort, searchQuery]
    );

    const abortRequestExamples = useCallback(() => {
        if (requestExamplesRef.current && requestExamplesRef.current.abort) {
            requestExamplesRef.current.abort();
        }
    }, []);

    const handleSortColumn = useCallback(
        (columnIndex: number) => {
            if (columnIndex === sort.column) {
                setSort(prevState => ({
                    ...prevState,
                    direction: sort.direction === Sort.Asc ? Sort.Desc :Sort.Asc,
                }));
            } else {
                setSort({
                    direction: Sort.Asc,
                    column: columnIndex,
                });
            }
        },
        [sort]
    );

    const handleChangeSearchQuery = useCallback((value: string) => setSearchQuery(value), []);

    const downloadExamples = useCallback(async () => {
        const notifyId = new Date().getTime();
        const notifyDuration = 2000;
        notify({
            id: notifyId,
            text: 'Downloading...',
        });

        if (fetchExportRef.current && downloadUrl) {
            const token = await fetchExportRef.current(getFetchOptions(0));

            if (token) {
                doDownload({ url: `${downloadUrl}?key=${token}` })
                    .then((response) => {
                        if (response) {
                            downloadFileFromBlobResponse(response);
                            notify({
                                text: 'Download was successful',
                                duration: notifyDuration,
                                id: notifyId,
                            });
                        }
                    })
                    .catch(() => {
                        notify({
                            text: 'Something went wrong',
                            id: notifyId,
                            error: true,
                            duration: notifyDuration,
                        });
                    });
            }
        }
    }, [notify, getFetchOptions, downloadUrl, doDownload]);

    const resetData = useCallback(() => {
        abortRequestExamples();
        setSearchQuery('');
        setTableData([]);
        skipRef.current = 0;
        setIsLoading(true);
        setTotalCountExamples(INITIAL_TOTAL_COUNT_EXAMPLES);
        setSort(DEFAULT_SORT_PARAMS);
    }, [abortRequestExamples]);

    const initialFetchDoneRef = useRef(false);
    const isFirstRenderRef = useRef(true);

    const doFetch = useCallback(async (searchVal: string, sortVal: SortParams, skip: number = 0) => {
        if (!isShown) return;

        const options: FetchOptions = {
            sortColumn: sortVal.column,
            search: searchVal,
            take: TAKE,
            skip,
            sortDir: sortVal.direction,
        };

        abortRequestExamples();
        requestExamplesRef.current = fetchRef.current(options);
        setIsLoading(true);

        try {
            const { data, totalRecords } = await requestExamplesRef.current;
            setTableData(prevState => {
                const newData = skip === 0 ? data : [...prevState, ...data];
                skipRef.current = newData.length;
                return newData;
            });
            setTotalCountExamples(totalRecords);
        } finally {
            setIsLoading(false);
        }
    }, [isShown, abortRequestExamples]);

    const debouncedSearch = useDebouncedCallback((searchVal: string, sortVal: SortParams) => {
        setTableData([]);
        skipRef.current = 0;
        doFetch(searchVal, sortVal, 0);
    }, 150);

    useEffect(() => {
        if (isShown) {
            initialFetchDoneRef.current = true;
            isFirstRenderRef.current = false;
            setTableData([]);
            skipRef.current = 0;
            doFetch(searchQuery, sort, 0);
        } else {
            initialFetchDoneRef.current = false;
            resetData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isShown]);

    useEffect(() => {
        if (isFirstRenderRef.current) {
            isFirstRenderRef.current = false;
            return;
        }
        if (isShown && initialFetchDoneRef.current) {
            setTotalCountExamples(INITIAL_TOTAL_COUNT_EXAMPLES);
            debouncedSearch(searchQuery, sort);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery]);

    useEffect(() => {
        if (isShown && initialFetchDoneRef.current) {
            setTotalCountExamples(INITIAL_TOTAL_COUNT_EXAMPLES);
            setTableData([]);
            skipRef.current = 0;
            doFetch(searchQuery, sort, 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sort]);

    const loadMore = useCallback(() => {
        doFetch(searchQuery, sort, skipRef.current);
    }, [doFetch, searchQuery, sort]);

    return {
        download: downloadExamples,
        isDownloading,
        tableData,
        sort,
        isLoading,
        totalCountExamples,
        getMenuExamples: loadMore,
        handleSortColumn,
        searchQuery,
        handleChangeSearchQuery,
    };
};
